import { useEffect, useCallback } from 'react';

export const useSpreadsheetKeyboard = ({
  // State dependencies
  activeCell,
  editingCell,
  visibleColumnNames,
  selectionStart,
  selectionEnd,
  filteredData,
  isMac,
  
  // Action functions
  enterEditMode,
  finishEditing,
  setActiveCell,
  setSelectionStart,
  setSelectionEnd,
  scrollToCell,
  extendSelection,
  moveActiveCell,
  copySelectedCells,
  cutSelectedCells,
  pasteSelectedCells,
  deleteSelectedCells,
  deleteSelectedRows,
  selectAllCells,
  undo,
  redo,
  
  // Additional action functions for shortcuts
  toggleButtonContainer,
  downloadAllSpreadsheets,
  onToggleAutomaticEntry,
  isCustomerView,
}) => {
  const handleKeyDown = useCallback(async (e) => {
    const modifierKey = isMac ? e.metaKey : e.ctrlKey;

    // Ignore keyboard events when focus is on input elements
    if (
      document.activeElement &&
      (document.activeElement.tagName === 'INPUT' ||
        document.activeElement.tagName === 'TEXTAREA' ||
        document.activeElement.tagName === 'SELECT' ||
        document.activeElement.classList.contains('react-select__input') ||
        document.activeElement.isContentEditable)
    ) {
      return;
    }

    // Start editing when typing a character
    if (
      activeCell &&
      !editingCell &&
      e.key.length === 1 &&
      !modifierKey &&
      !e.altKey
    ) {
      e.preventDefault();
      // Prevent editing for customer users
      if (isCustomerView) {
        return;
      }
      const [row, col] = activeCell;
      enterEditMode(row, col, e.key, true);
      return;
    }

    // Handle Enter key
    if (e.key === 'Enter') {
      e.preventDefault();
      if (editingCell) {
        finishEditing();
      }

      if (activeCell) {
        const [row, col] = activeCell;
        let newRow = row;
        let newCol = col;

        if (e.shiftKey) {
          newRow = Math.max(0, row - 1);
        } else {
          if (row < filteredData.length - 1) {
            newRow = row + 1;
          }
        }

        setActiveCell([newRow, newCol]);
        setSelectionStart([newRow, newCol]);
        setSelectionEnd([newRow, newCol]);
        
        setTimeout(() => {
          scrollToCell(newRow, newCol);
        }, 0);
      } else {
        setActiveCell([0, 0]);
        setSelectionStart([0, 0]);
        setSelectionEnd([0, 0]);
        scrollToCell(0, 0);
      }
      return;
    }

    // Handle Delete/Backspace
    if (e.key === 'Backspace' || e.key === 'Delete') {
      if (!editingCell && (selectionStart || activeCell)) {
        e.preventDefault();
        if (
          selectionStart &&
          selectionEnd &&
          selectionStart[1] === 0 &&
          selectionEnd[1] === Object.keys(visibleColumnNames).length - 1
        ) {
          deleteSelectedRows();
        } else {
          await deleteSelectedCells();
        }
      }
      return;
    }

    // Handle arrow keys with Shift (extend selection)
    if (
      e.shiftKey &&
      activeCell &&
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      e.preventDefault();
      extendSelection(e.key);
      return;
    }

    // Handle arrow keys (move active cell)
    if (
      !editingCell &&
      ['ArrowUp', 'ArrowDown', 'ArrowLeft', 'ArrowRight'].includes(e.key)
    ) {
      e.preventDefault();
      moveActiveCell(e.key);
      return;
    }

    // Handle Tab navigation
    if (e.key === 'Tab') {
      e.preventDefault();
      if (editingCell) {
        finishEditing();
      }

      if (activeCell) {
        const [row, col] = activeCell;
        let newRow = row;
        let newCol = col;

        if (e.shiftKey) {
          if (col > 0) {
            newCol = col - 1;
          } else if (row > 0) {
            newRow = row - 1;
            newCol = Object.keys(visibleColumnNames).length - 1;
          }
        } else {
          if (col < Object.keys(visibleColumnNames).length - 1) {
            newCol = col + 1;
          } else if (row < filteredData.length - 1) {
            newRow = row + 1;
            newCol = 0;
          }
        }

        setActiveCell([newRow, newCol]);
        setSelectionStart([newRow, newCol]);
        setSelectionEnd([newRow, newCol]);
        
        setTimeout(() => {
          scrollToCell(newRow, newCol);
        }, 0);
      } else {
        setActiveCell([0, 0]);
        setSelectionStart([0, 0]);
        setSelectionEnd([0, 0]);
        scrollToCell(0, 0);
      }
      return;
    }

    // Handle copy/cut/paste
    if (modifierKey && e.key === 'c') {
      e.preventDefault();
      copySelectedCells();
      return;
    }

    if (modifierKey && e.key === 'x') {
      e.preventDefault();
      cutSelectedCells();
      return;
    }

    if (modifierKey && e.key === 'v') {
      e.preventDefault();
      await pasteSelectedCells();
      return;
    }

    // Handle undo/redo (disabled for customer users)
    if (modifierKey && e.key === 'z' && !isCustomerView) {
      e.preventDefault();
      if (e.shiftKey) {
        await redo();
      } else {
        await undo();
      }
      return;
    }

    if (!isMac && modifierKey && e.key === 'y' && !isCustomerView) {
      e.preventDefault();
      await redo();
      return;
    }

    // Handle select all
    if (modifierKey && e.key === 'a') {
      if (!editingCell) {
        e.preventDefault();
        selectAllCells(filteredData, visibleColumnNames);
      }
      return;
    }

    // Handle toggle button container (⌘K)
    if (modifierKey && e.key === 'k') {
      e.preventDefault();
      if (toggleButtonContainer) {
        toggleButtonContainer();
      }
      return;
    }

    // Handle download (⌘D)
    if (modifierKey && e.key === 'd') {
      e.preventDefault();
      if (downloadAllSpreadsheets) {
        downloadAllSpreadsheets();
      }
      return;
    }

    // Handle data entry (⌘E)
    if (modifierKey && e.key === 'e') {
      e.preventDefault();
      if (onToggleAutomaticEntry) {
        onToggleAutomaticEntry();
      }
      return;
    }
  }, [
    activeCell,
    editingCell,
    visibleColumnNames,
    selectionStart,
    selectionEnd,
    filteredData,
    isMac,
    enterEditMode,
    finishEditing,
    setActiveCell,
    setSelectionStart,
    setSelectionEnd,
    scrollToCell,
    extendSelection,
    moveActiveCell,
    copySelectedCells,
    cutSelectedCells,
    pasteSelectedCells,
    deleteSelectedCells,
    deleteSelectedRows,
    selectAllCells,
    undo,
    redo,
    toggleButtonContainer,
    downloadAllSpreadsheets,
    onToggleAutomaticEntry,
  ]);

  useEffect(() => {
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [handleKeyDown]);
};